/**
 * L'évolution d'un exercice — **une courbe à la fois, choisie parmi cinq**
 * (`ARCHITECTURE_V2.md` §6.3).
 *
 * 🔴 **SVG écrit à la main**, comme `WeightChart` depuis l'étape 8 — et pour
 * les raisons qu'elle avait déjà pesées : Recharts (~120 ko compressés)
 * doublerait le bundle, Chart.js rend dans un `<canvas>` donc échappe au banc.
 * Le §6.3 rouvre la question puisque la V2 en demande une dizaine, et y répond :
 * les formes nécessaires sont peu nombreuses, et **trois primitives réutilisées
 * dix fois ne font pas dix graphiques**. Celui-ci partage `niceTicks`,
 * `toEpochDay` et l'ouverture de fenêtre avec `WeightChart`
 * (`lib/chartScale.ts`).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 UNE COURBE À LA FOIS, ET UN SÉLECTEUR — REVIREMENT DU 06/09/2026
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Demandé après usage sur l'appareil : « affiche un seul graphe et en dessous
 * plusieurs boutons pour sélectionner le graphe à afficher ». Cinq lectures :
 * **plus gros poids · max en une rep · meilleur volume de série · volume de
 * séance · total de répétitions**.
 *
 * ⚠️ **Ce que ça remplace, et pourquoi ce n'est pas un reniement.** L'écran
 * empilait deux cadres depuis le 29/08 — charge en haut, volume en bas — au
 * motif, toujours valable, que « superposer des kilos et des répétitions ferait
 * se croiser deux courbes dont seule la mise à l'échelle a écrit l'histoire ».
 * Cette règle-là n'est pas relâchée : elle est rendue **sans objet**. Deux
 * grandeurs qui ne partagent pas une échelle ne se superposent toujours pas —
 * elles se **succèdent** désormais dans le même cadre, ce qui ne fait jamais
 * cohabiter deux unités à l'écran.
 *
 * 🔴 **Et ce que l'empilement faisait bien, le sélecteur le fait mieux.**
 * L'argument de l'époque était : « monter de 60 à 62,5 kg en passant de 4×10 à
 * 3×6, c'est descendre, et il faut deux tracés pour le voir ». C'est vrai — mais
 * deux cadres de 92 px l'un sous l'autre en montraient deux, quand la question
 * en demande cinq. **Volume de séance** répond à elle seule à cet exemple, et
 * mieux que la paire : c'est le produit, pas les deux facteurs à rapprocher à
 * l'œil.
 *
 * ⚠️ **L'axe du temps reste calculé sur toutes les séances**, pas sur les
 * points de la métrique affichée : c'est ce qui fait que changer de courbe ne
 * déplace pas l'axe sous les doigts.
 *
 * ⚠️ **Aucune droite de régression, aucun coefficient** — règle générale du
 * §6.2, posée une fois pour toutes les statistiques : « l'application mesure,
 * l'utilisateur conclut ».
 */

import { useState } from 'react';

import type { LastPassageDTO } from '../../../../shared/types';
import { ChartTooltip } from '../../components/ui/ChartTooltip';
import { useChartSweep } from '../../hooks/useChartSweep';
import { useElementWidth } from '../../hooks/useElementWidth';
import { nearestIndex, niceTicks, toEpochDay, verticalWindow } from '../../lib/chartScale';
import {
  bestSetVolumeOf,
  estimatedOneRepMaxOf,
  heaviestWeightOf,
  sessionVolumeOf,
  totalRepsOf,
} from './exercisePassage';

const MARGIN = { top: 8, right: 10, bottom: 18, left: 34 };
const PLOT_HEIGHT = 92;

/** `2026-09-05` → `05/09`. Hissée au module : les deux tracés et l'axe la lisent. */
const jour = (date: string) => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

interface Point {
  date: string;
  value: number;
}

interface Serie {
  key: string;
  label: string;
  color: string;
  points: Point[];
  /**
   * La valeur telle qu'elle se lit dans l'infobulle, **unité comprise**.
   *
   * ⚠️ Elle vient de la série et non du tracé : le libellé au-dessus annonce
   * déjà « (kg) » ou « (répétitions) », mais une bulle posée sur un point est
   * lue seule, souvent le doigt masquant le titre. Un nombre nu s'y lirait dans
   * la mauvaise unité une fois sur deux — l'exercice en durée compte des
   * secondes là où son voisin compte des reps.
   */
  format: (value: number) => string;
}

/**
 * Un tracé : ses graduations, sa ligne, ses points.
 *
 * ⚠️ **`x` vient de l'appelant**, pas de la série : c'est ce qui garantit que
 * les deux tracés sont **alignés dans le temps**. Chacun calculant son propre
 * axe horizontal, deux séances absentes d'une série décaleraient tout, et l'on
 * comparerait des dates différentes en croyant lire la même.
 */
function Plot({
  serie,
  width,
  x,
}: {
  serie: Serie;
  width: number;
  x: (date: string) => number;
}) {
  /*
   * 🔴 **Une sélection PAR TRACÉ, pas une pour les deux** *(05/09/2026)*. Les
   * deux cadres partagent l'axe du temps, mais pas leurs points : une séance au
   * poids du corps est dans le volume et absente de la charge. Une sélection
   * commune devrait donc décider quoi faire quand la date désignée n'existe pas
   * dans l'autre série — désigner le voisin, ou ne rien montrer —, c'est-à-dire
   * inventer une règle pour un cas qui n'a pas de bonne réponse. Chaque tracé
   * répond de ses propres points.
   *
   * ⚠️ **Une date, jamais un rang** — même raison que la courbe de poids :
   * l'identité d'un point est sa donnée, pas sa position. Ici la série ne change
   * pas sous la sélection, mais retenir un index serait un piège posé pour le
   * jour où elle changerait (un filtre de période, par exemple).
   */
  const [activeDate, setActiveDate] = useState<string | null>(null);

  const height = MARGIN.top + PLOT_HEIGHT + MARGIN.bottom;
  const { min, max } = verticalWindow(serie.points.map((point) => point.value));
  const ticks = niceTicks(min, max);

  const y = (value: number): number =>
    MARGIN.top + PLOT_HEIGHT - ((value - min) / (max - min)) * PLOT_HEIGHT;

  const chemin = serie.points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'}${x(point.date).toFixed(1)} ${y(point.value).toFixed(1)}`,
    )
    .join(' ');

  /**
   * Le doigt désigne une **date**, jamais un disque de 3 px — `nearestIndex`,
   * la même primitive que la courbe de poids (§6.3). Viser un point à 393 px de
   * large serait illusoire.
   *
   * ⚠️ `touch-action: pan-y` : le défilement vertical reste au navigateur. Sans
   * lui, la fiche d'exercice cesserait de défiler dès qu'on la touche par le
   * graphique — et cette fiche s'ouvre en feuille, donc c'est le seul moyen
   * d'atteindre ce qui est en dessous.
   *
   * ⚠️ **Le retrait n'a lieu qu'au `pointerdown`** : pendant un glissement,
   * repasser sur le point sélectionné l'éteindrait sous les doigts.
   */
  const { attacher, handlers } = useChartSweep((cx, _cy, toggle) => {
    const found = nearestIndex(
      serie.points.map((point) => x(point.date)),
      cx,
    );
    const date = found === null ? null : (serie.points[found]?.date ?? null);
    if (date === null) return;
    setActiveDate((current) => (toggle && current === date ? null : date));
  });

  const designe = serie.points.find((point) => point.date === activeDate) ?? null;

  return (
    <div className="n-exo-chart__plot">
      <p className="n-eyebrow">{serie.label}</p>
      {/*
        ⚠️ **Le conteneur positionné n'enveloppe que le SVG**, pas le libellé
        au-dessus : sinon l'ordonnée de la bulle serait décalée de la hauteur de
        ce libellé, et elle désignerait un point qui n'est pas celui qu'on a
        touché.
      */}
      <div className="n-chart-host">
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`${serie.label}, de ${serie.points[0]?.date ?? ''} à ${
            serie.points.at(-1)?.date ?? ''
          }`}
          style={{ display: 'block', touchAction: 'pan-y' }}
          ref={attacher}
          {...handlers}
        >
          {/*
            🔴 **Le trait vertical du point désigné** *(06/09/2026, demandé
            après usage)* : « affiche une ligne verticale au niveau du point
            pour visualiser correctement le point sélectionné ».

            ⚠️ **Dessiné avant la courbe**, donc dessous : un repère qui passe
            par-dessus la mesure la masque au moment précis où on la regarde.
          */}
          {designe !== null && (
            <line
              x1={x(designe.date)}
              x2={x(designe.date)}
              y1={MARGIN.top}
              y2={MARGIN.top + PLOT_HEIGHT}
              className="n-chart__crosshair"
            />
          )}

          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={MARGIN.left}
                x2={width - MARGIN.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke="var(--line)"
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 6}
                y={y(tick) + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--ink-soft)"
              >
                {/*
                  ⚠️ **Virgule décimale, comme partout ailleurs dans l'app.**
                  L'axe écrivait « 62.5 » quand l'infobulle posée dessus écrit
                  « 62,5 kg » : deux conventions sur le même graphique, le
                  défaut déjà consigné pour `toField` (`lib/formFields.ts`).
                */}
                {tick.toLocaleString('fr-FR')}
              </text>
            </g>
          ))}

          <path
            d={chemin}
            fill="none"
            stroke={serie.color}
            strokeWidth={2}
            strokeLinejoin="round"
          />

          {serie.points.map((point) => (
            <circle
              key={point.date}
              cx={x(point.date)}
              cy={y(point.value)}
              r={3}
              fill={serie.color}
            />
          ))}

          {/*
            ⚠️ **Anneau de la couleur du FOND** sur le point désigné, comme la
            courbe de poids : un disque simplement grossi se confond avec ses
            voisins là où la courbe est dense, et disparaît là où elle croise
            une graduation.
          */}
          {designe && (
            <circle
              cx={x(designe.date)}
              cy={y(designe.value)}
              r={5}
              fill={serie.color}
              stroke="var(--surface)"
              strokeWidth={2}
            />
          )}
        </svg>

        {designe && (
          <ChartTooltip
            x={x(designe.date)}
            y={y(designe.value)}
            boxWidth={width}
            boxHeight={height}
          >
            {jour(designe.date)}
            <br />
            {serie.format(designe.value)}
          </ChartTooltip>
        )}
      </div>
    </div>
  );
}

/**
 * Les cinq lectures d'un historique d'exercice.
 *
 * 🔴 **Elles ne se superposent pas, elles se CHOISISSENT** *(06/09/2026,
 * demandé après usage sur l'appareil)*. Cf. l'en-tête du fichier : c'est le
 * revirement de l'étape, et il ne relâche pas la règle des deux axes — il la
 * rend sans objet.
 *
 * ⚠️ **L'ordre n'est pas indifférent.** Il va du plus concret au plus composé :
 * une charge se lit sur la barre, un maximum estimé se calcule, un volume
 * s'additionne. C'est aussi l'ordre dans lequel on se pose les questions devant
 * une fiche — « je montais à combien ? », puis « je vaux combien ? », puis
 * « j'en ai fait combien ? ».
 */
const METRIQUES: readonly {
  key: string;
  /** Sur le bouton — court, parce qu'ils sont cinq sur 375 px. */
  bouton: string;
  /** Au-dessus du tracé — le nom entier, celui qu'on lit une fois entré. */
  titre: string;
  color: string;
  lire: (passage: LastPassageDTO) => number | null;
  format: (value: number) => string;
}[] = [
  {
    key: 'poids',
    bouton: 'Poids max',
    titre: 'Plus gros poids',
    color: 'var(--brand)',
    lire: heaviestWeightOf,
    format: (value) => `${value.toLocaleString('fr-FR')} kg`,
  },
  {
    key: '1rm',
    bouton: 'Max 1 rep',
    /*
     * 🔴 **« estimé » est dans le titre, et ce n'est pas décoratif.** Un 1RM
     * calculé et un 1RM soulevé ne sont pas la même chose, et le second n'est
     * jamais mesuré ici. Le mot est la seule chose qui empêche cette courbe
     * d'être lue comme un historique de records.
     */
    titre: 'Max en une rep (estimé)',
    color: 'var(--brand)',
    lire: estimatedOneRepMaxOf,
    format: (value) => `${value.toLocaleString('fr-FR')} kg`,
  },
  {
    key: 'meilleure-serie',
    bouton: 'Meilleure série',
    titre: 'Meilleur volume de série',
    color: 'var(--brand)',
    lire: bestSetVolumeOf,
    format: (value) => `${value.toLocaleString('fr-FR')} kg`,
  },
  {
    key: 'volume',
    bouton: 'Volume séance',
    titre: 'Volume de séance',
    color: 'var(--brand)',
    lire: sessionVolumeOf,
    format: (value) => `${value.toLocaleString('fr-FR')} kg`,
  },
  {
    key: 'reps',
    bouton: 'Total reps',
    titre: 'Total de répétitions',
    color: 'var(--brand)',
    lire: (passage) => totalRepsOf(passage)?.value ?? null,
    format: (value) => `${value.toLocaleString('fr-FR')} reps`,
  },
];

export function ExerciseChart({ history }: { history: readonly LastPassageDTO[] }) {
  const { ref, width } = useElementWidth();
  const [choisie, setChoisie] = useState<string | null>(null);

  /*
   * ⚠️ **Du plus ancien au plus récent** : le serveur rend l'historique dans
   * l'ordre inverse (le passage précédent d'abord, c'est ce dont la séance a
   * besoin). Un axe du temps se lit de gauche à droite.
   */
  const passages = [...history].reverse();

  /*
   * ⚠️ **L'unité du total suit la nature de l'exercice** — des répétitions pour
   * un exercice qui s'en compte, des secondes pour un gainage. On lit celle du
   * passage le plus récent : c'est ce que l'exercice est *aujourd'hui*.
   */
  const uniteTotal = passages.map(totalRepsOf).filter((v) => v !== null).at(-1)?.unit ?? 'reps';

  /*
   * ⚠️ **Deux points au minimum, par métrique.** Un seul ne dessine pas une
   * évolution : il dessinerait un point au milieu d'un cadre vide, ce qui a
   * l'air d'un graphique cassé. 🔴 **Une métrique sans assez de points n'a donc
   * pas de bouton** — plutôt qu'un bouton qui ouvre un cadre vide. C'est le cas
   * courant d'un exercice au poids du corps : ni charge, ni maximum, ni volume,
   * mais bien un total de répétitions.
   */
  const disponibles: Serie[] = METRIQUES.map((metrique) => ({
    key: metrique.key,
    label: metrique.key === 'reps' && uniteTotal === 's' ? 'Total tenu' : metrique.titre,
    color: metrique.color,
    points: passages
      .map((passage) => ({ date: passage.date, value: metrique.lire(passage) }))
      .filter((point): point is Point => point.value !== null),
    format:
      metrique.key === 'reps' && uniteTotal === 's'
        ? (value: number) => `${value.toLocaleString('fr-FR')} s tenues`
        : metrique.format,
  })).filter((serie) => serie.points.length >= 2);

  /*
   * 🔴 **C'est ici, et nulle part ailleurs, qu'on décide qu'il n'y a rien à
   * tracer** — et le composant le DIT plutôt que de rendre `null`. Il rendait
   * `null`, et l'écran appelant portait son propre pré-test pour afficher la
   * phrase ; les deux règles ont divergé le jour où les métriques ont changé.
   * Une seule décision, à l'endroit qui a les données sous la main.
   */
  if (disponibles.length === 0) {
    return (
      <p className="n-list__meta" style={{ margin: 0 }}>
        {passages.length <= 1
          ? 'Une seule séance pour l’instant : les courbes apparaîtront à la deuxième.'
          : 'Pas encore de quoi tracer une courbe sur cet exercice.'}
      </p>
    );
  }

  /*
   * 🔴 **La métrique affichée est dérivée au rendu, jamais posée dans un
   * effet.** Un `useEffect` de pré-sélection serait refusé par
   * `react-hooks/set-state-in-effect`, à juste titre : il ferait un rendu de
   * plus, et surtout il garderait une métrique devenue indisponible après un
   * changement d'exercice — on afficherait alors un cadre vide sans que rien ne
   * l'explique. En dérivant, une sélection qui n'existe plus retombe seule sur
   * la première disponible.
   */
  const serie = disponibles.find((s) => s.key === choisie) ?? disponibles[0];
  if (serie === undefined) return null;

  /*
   * 🔴 **L'axe du temps est calculé sur TOUTES les séances**, pas sur les seuls
   * points de la métrique affichée. C'est ce qui fait que changer de métrique ne
   * déplace pas l'axe : les cinq courbes décrivent les mêmes séances, et une
   * séance sans charge (poids du corps) laisse un trou plutôt que de resserrer
   * l'échelle sous les doigts.
   */
  const dates = passages.map((passage) => toEpochDay(passage.date));
  const premier = Math.min(...dates);
  const etendue = Math.max(...dates) - premier;
  const plotWidth = Math.max(0, width - MARGIN.left - MARGIN.right);

  const x = (date: string): number =>
    etendue === 0
      ? MARGIN.left + plotWidth / 2
      : MARGIN.left + ((toEpochDay(date) - premier) / etendue) * plotWidth;

  return (
    <div ref={ref} className="n-exo-chart">
      {/*
        🔴 **Dessiné en pixels RÉELS mesurés, pas dans un `viewBox` étiré** —
        la leçon de `WeightChart` à l'étape 8 : un `viewBox` mis à l'échelle
        ferait passer les libellés d'axe de 10 px à 18 px sur la colonne du
        bureau. D'où `useElementWidth`.
      */}
      {width > 0 && (
        <>
          {/*
            ⚠️ **Une `key` sur la métrique, et elle compte** : elle remonte le
            tracé au changement, ce qui **solde la sélection d'un point**. Sans
            elle, le point désigné sur « Poids max » resterait allumé sur
            « Total reps », où il ne désigne plus la même grandeur — la leçon de
            la sélection qui survit au changement de plage (05/09), prise par
            l'autre bout.
          */}
          <Plot key={serie.key} serie={serie} width={width} x={x} />

          {/*
            ⚠️ **Les dates sont posées UNE fois, sous le tracé.**
            ⚠️ **Deux dates seulement, aux extrémités** : une par point
            chevaucherait dès cinq séances, c'est-à-dire au moment précis où la
            courbe devient intéressante.
          */}
          <div className="n-exo-chart__axis tnum">
            <span>{jour(passages[0]?.date ?? '')}</span>
            <span>{jour(passages.at(-1)?.date ?? '')}</span>
          </div>
        </>
      )}

      {/*
        🔴 **Le sélecteur est SOUS le graphique, et il n'y est pas par défaut.**
        Au-dessus, il aurait repoussé la courbe — la seule chose qu'on vient
        voir — d'un rang entier sur un écran de 375 px. C'est l'arbitrage déjà
        tenu par les plages de l'écran de poids, en sens inverse : là-bas la
        rangée gouverne toute la carte et se pose en tête ; ici elle ne
        gouverne que le cadre qu'elle suit.

        ⚠️ **Il se replie sur deux rangs plutôt que de glisser**, contrairement
        aux puces de muscles de l'étape 4. La raison tient au nombre : dix-sept
        puces qui défilent, c'est une liste ; **cinq boutons, on doit tous les
        voir** pour savoir ce qu'on peut demander. Un cinquième caché derrière
        le bord serait une métrique qui n'existe pas.

        ⚠️ Un seul bouton disponible : la rangée disparaît. Choisir entre une
        chose n'est pas un choix.
      */}
      {disponibles.length > 1 && (
        <div className="n-exo-chart__pick">
          {disponibles.map((option) => {
            const metrique = METRIQUES.find((m) => m.key === option.key);
            const actif = option.key === serie.key;
            return (
              <button
                key={option.key}
                type="button"
                className={actif ? 'is-active' : undefined}
                aria-pressed={actif}
                onClick={() => {
                  setChoisie(option.key);
                }}
              >
                {option.key === 'reps' && uniteTotal === 's'
                  ? 'Total tenu'
                  : (metrique?.bouton ?? option.label)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
